
import { useState, useEffect } from 'react';
import { ShoppingCartIcon, CloseIcon } from './Icons';
import { BsInfoCircleFill } from "react-icons/bs";
import axios from 'axios';

const CartSidebar = ({
    sentItems = {},
    setSentItems = () => { },
    isMobileView,
    onClose,
    cartItemsCount,
    cartItems,
    handleClearCart,
    tableCode,
    currentSeatingId,
    openScheduleModal,
    showPaymentData,
    orderId,
    setShowPaymentData,
    fetchCartDetails,
    decrementQuantity,
    incrementQuantity,
    notes,
    setNotes,
    handleSendToChef,
    subTotal,
    handleShowGST,
    gstTotal,
    serviceApplied,
    toggleServiceCharges,
    serviceCharges,
    discountAmount,
    grandTotal,
    showPaymentOptions,
    setShowPaymentOptions,
    allReady,
    selectedPaymentMethod,
    isWalletChecked,
    handleCheckboxChange,
    showWalletOptions,
    selectedWalletType,
    setSelectedWalletType,
    availableBalance,
    availableSelf,
    availableCompany,
    availableCashback,
    isCashChecked,
    isOnlineChecked,
    orderPlacing,
    handleCheckoutClick,
    handleWalletPayment,
    toast,
    handleDeleteItem,
}) => {

    const getItemKey = (item) => item?.cartItemId ?? item?.itemId;
    const [loadingItems, setLoadingItems] = useState({});

    // ===== ORIGINAL FUNCTIONS =====
    const resetItemStatus = async (item) => {
        const itemId = getItemKey(item);
        const token = localStorage.getItem("authToken");
        if (!token || !itemId) return;

        try {
            await axios.put(
                `https://backend.maamaas.com/food/api/cart/cartitem/status/${itemId}`,
                null,
                {
                    params: {
                        status: "PENDING",
                        note: "",
                        quantity: item.quantity ?? 1
                    },
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
        } catch (err) {
            console.error(err);
        }
    };

    const handleIncrementQuantity = async (item) => {
        const itemId = getItemKey(item);
        await incrementQuantity(item);

        setSentItems(prev => {
            const updated = { ...prev };
            delete updated[itemId];
            localStorage.setItem("sentItems", JSON.stringify(updated));
            return updated;
        });

        await resetItemStatus(item);
        setTimeout(fetchCartDetails, 500);
    };

    const handleDecrementQuantity = async (item) => {
        const itemId = getItemKey(item);
        const currentQty = item.quantity || 1;

        if (currentQty === 1) {
            await handleDeleteItem(item);
            return;
        }

        await decrementQuantity(item);

        setSentItems(prev => {
            const updated = { ...prev };
            delete updated[itemId];
            localStorage.setItem("sentItems", JSON.stringify(updated));
            return updated;
        });

        await resetItemStatus(item);
        setTimeout(fetchCartDetails, 500);
    };

    const shouldShowSendButton = (item) => {
        const status = item.status || item.orderStatus;
        const isCompleted = ["CONFIRMED", "COMPLETED", "DELIVERED"]
            .includes(status?.trim().toUpperCase());

        const itemId = getItemKey(item);
        if (isCompleted) return false;
        if (sentItems[itemId]) return false;

        return true;
    };

    const getSendButtonText = (item) => {
        const itemId = getItemKey(item);

        if (loadingItems[itemId]) return "Processing...";
        if (sentItems[itemId]) return "Sent ✅";

        const status = item.status || item.orderStatus;
        const isCompleted = ["CONFIRMED", "COMPLETED", "DELIVERED"]
            .includes(status?.trim().toUpperCase());

        if (isCompleted) return "Sent ✅";
        return "Send";
    };

    const isSendButtonDisabled = (item) => {
        const itemId = getItemKey(item);
        if (loadingItems[itemId]) return true;

        const status = item.status || item.orderStatus;
        const isCompleted = ["CONFIRMED", "COMPLETED", "DELIVERED"]
            .includes(status?.trim().toUpperCase());

        return isCompleted || sentItems[itemId];
    };

    const handleSendToChefWrapper = async (item) => {
        const itemId = getItemKey(item);

        if (sentItems[itemId]) {
            toast.error("Item already sent to chef");
            return;
        }

        const status = item.status || item.orderStatus;
        const isCompleted = ["CONFIRMED", "COMPLETED", "DELIVERED"]
            .includes(status?.trim().toUpperCase());

        if (isCompleted) {
            toast.error("Item already sent to chef");
            return;
        }

        await handleSendToChef(item, setLoadingItems);
    };

    const isAllDelivered = cartItems.length > 0 && cartItems.every((item) => {
        const status = item.status || item.orderStatus;
        return ["DELIVERED", "COMPLETED"].includes(status?.trim().toUpperCase());
    });

    const pendingItems = cartItems.filter((item) => {
        const status = item.status || item.orderStatus;
        return !["DELIVERED", "COMPLETED"].includes(status?.trim().toUpperCase());
    });

    return (
        <div className={`cart-sidebar ${isMobileView ? 'cart-sidebar--mobile' : 'cart-sidebar--desktop'}`}>

            {/* HEADER */}
            <div className="cart-header">
                <div className="cart-header-left">
                    <ShoppingCartIcon />
                    <span className="cart-title">Your Cart</span>
                    <span className="cart-badge">{cartItemsCount}</span>
                </div>
                <div className="cart-header-right">
                    {cartItems.length > 0 && (
                        <button className="cart-clear-btn" onClick={handleClearCart}>
                            Clear All
                        </button>
                    )}
                    {isMobileView && (
                        <button className="cart-close-btn" onClick={onClose}><CloseIcon /></button>
                    )}
                </div>
            </div>

            {/* TABLE INFO */}
            {(tableCode || currentSeatingId) && (
                <div className="cart-table-banner">
                    <span>🍽️ Table: <strong>{tableCode || '—'}</strong></span>
                    {currentSeatingId && <span className="cart-session-badge">Active Session</span>}
                </div>
            )}

            {!currentSeatingId && (
                <div className="cart-no-table-notice">
                    <span>📋 No table selected yet</span>
                    <p>Select a table to start adding items to cart</p>
                    <button className="cart-book-btn" onClick={openScheduleModal}>Book a Table</button>
                </div>
            )}

            <div className="cart-body">
                {showPaymentData ? (
                    <div className="cart-order-success">
                        <div className="cart-success-icon">✅</div>
                        <h3>Order Placed!</h3>
                        <p>Order ID: <strong>{orderId}</strong></p>
                        <button
                            className="cart-continue-btn"
                            onClick={() => {
                                setShowPaymentData(false);
                                fetchCartDetails();
                                if (onClose) onClose();
                            }}
                        >
                            Continue Shopping
                        </button>
                    </div>
                ) : cartItems.length === 0 ? (
                    <div className="cart-empty">
                        <div className="cart-empty-icon"><ShoppingCartIcon /></div>
                        <p>Your cart is empty</p>
                        <span>Add delicious items from the menu</span>
                    </div>
                ) : (
                    <>
                        {/* ITEMS */}
                        <div className="cart-items-list">
                            {cartItems.map(item => {
                                const qty = item.quantity || 1;
                                const price = parseFloat(item.effectivePrice) || parseFloat(item.price) || 0;
                                const itemId = getItemKey(item);

                                const status = item.status || item.orderStatus;
                                const isCompleted = ["CONFIRMED", "COMPLETED", "DELIVERED"]
                                    .includes(status?.trim().toUpperCase());

                                return (
                                    <div key={itemId} className="cart-item">
                                        <div className="cart-item-top">
                                            <img src={item.dishImage} className="cart-item-img" />
                                            <div className="cart-item-info">
                                                <div className="cart-item-name">{item.dishName}</div>
                                                <div className="cart-item-price">₹{price.toFixed(2)}</div>
                                                {isCompleted && <div className="cart-item-sent">✓ Sent to kitchen</div>}
                                            </div>


                                            <div className="cart-qty-controls">
                                                {!isCompleted ? (
                                                    <>
                                                        <button
                                                            className="cart-qty-btn"
                                                            onClick={() => {
                                                                const quantity = Number(qty);
                                                                if (quantity <= 1) {
                                                                    handleDeleteItem?.(item);
                                                                } else {
                                                                    handleDecrementQuantity(item);
                                                                }
                                                            }}
                                                        >
                                                            {Number(qty) <= 1 ? '🗑' : '−'}
                                                        </button>

                                                        <span className="cart-qty-num">{qty}</span>

                                                        <button
                                                            className="cart-qty-btn"
                                                            onClick={() => handleIncrementQuantity(item)}
                                                        >
                                                            +
                                                        </button>
                                                    </>
                                                ) : (
                                                    <span className="cart-item-status-badge">✓ Sent to Kitchen</span>
                                                )}
                                            </div>
                                        </div>

                                        {shouldShowSendButton(item) && (
                                            <div className="cart-item-actions">
                                                <input
                                                    type="text"
                                                    placeholder="Special instructions..."
                                                    value={notes[item.itemId] || ""}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        const itemIdKey = item.itemId;

                                                        setNotes(prev => ({
                                                            ...prev,
                                                            [itemIdKey]: value
                                                        }));

                                                        setSentItems(prev => {
                                                            const updated = { ...prev };
                                                            delete updated[getItemKey(item)];
                                                            localStorage.setItem("sentItems", JSON.stringify(updated));
                                                            return updated;
                                                        });
                                                    }}
                                                    className="cart-note-input"
                                                />

                                                <button
                                                    className="cart-send-btn"
                                                    disabled={isSendButtonDisabled(item)}
                                                    onClick={() => handleSendToChefWrapper(item)}
                                                >
                                                    {getSendButtonText(item)}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* ✅ TOP SUBTOTAL ONLY BEFORE BILL */}
                        {!showPaymentOptions && (
                            <div className="cart-summary">
                                <div className="cart-summary-row">
                                    <span>Sub Total</span>
                                    <span>₹{subTotal.toFixed(2)}</span>
                                </div>
                            </div>
                        )}

                        {/* BILL BUTTON */}
                        {!showPaymentOptions ? (
                            <button
                                onClick={() => setShowPaymentOptions(true)}
                                disabled={!isAllDelivered}
                                className={`cart-bill-btn ${isAllDelivered
                                    ? 'cart-bill-btn--active'
                                    : 'cart-bill-btn--disabled'
                                    }`}
                            >
                                {isAllDelivered
                                    ? '🧾 Generate Bill'
                                    : `⏳ ${pendingItems.length} item(s) pending`}
                            </button>
                        ) : (
                            <>
                                {/* ✅ FULL SUMMARY AFTER CLICK */}
                                <div className="cart-summary">
                                    <div className="cart-summary-row">
                                        <span>Sub Total</span>
                                        <span>₹{subTotal.toFixed(2)}</span>
                                    </div>
                                    <div className="cart-summary-row">
                                        <span>GST <BsInfoCircleFill onClick={handleShowGST} /></span>
                                        <span>₹{gstTotal.toFixed(2)}</span>
                                    </div>
                                    <div className="cart-summary-row">
                                        <span>
                                            Service Charges
                                            <button className="cart-toggle-service" onClick={toggleServiceCharges}>
                                                {serviceApplied ? 'Remove' : 'Apply'}
                                            </button>
                                        </span>
                                        <span>₹{serviceCharges.toFixed(2)}</span>
                                    </div>
                                    <div className="cart-summary-total">
                                        <span>Grand Total</span>
                                        <span>₹{grandTotal.toFixed(2)}</span>
                                    </div>
                                </div>

                                {/* PAYMENT FULL SAME */}
                                <div className="cart-payment-section">
                                    <div className="cart-payment-title">Select Payment Method</div>

                                    <div className="cart-payment-options">

                                        {/* WALLET */}
                                        <div
                                            className={`cart-payment-option ${selectedPaymentMethod === 'Maamaas_Wallet' ? 'selected' : ''}`}
                                            onClick={() => handleCheckboxChange('wallet', !isWalletChecked)}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isWalletChecked}
                                                onChange={(e) => handleCheckboxChange('wallet', e.target.checked)}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                            <span>👛 Wallet (₹{availableBalance.toFixed(2)})</span>
                                        </div>

                                        {/* WALLET TYPES */}
                                        {showWalletOptions && (
                                            <div className="cart-wallet-options">
                                                <label>
                                                    <input type="radio" value="self"
                                                        checked={selectedWalletType === "self"}
                                                        onChange={(e) => setSelectedWalletType(e.target.value)} />
                                                    Self (₹{availableSelf.toFixed(2)})
                                                </label>

                                                <label>
                                                    <input type="radio" value="company"
                                                        checked={selectedWalletType === "company"}
                                                        onChange={(e) => setSelectedWalletType(e.target.value)} />
                                                    Company (₹{availableCompany.toFixed(2)})
                                                </label>

                                                <label>
                                                    <input type="radio" value="cashback"
                                                        checked={selectedWalletType === "cashback"}
                                                        onChange={(e) => setSelectedWalletType(e.target.value)} />
                                                    Cashback (₹{availableCashback.toFixed(2)})
                                                </label>
                                            </div>
                                        )}

                                        {/* ONLINE */}
                                        <div
                                            className={`cart-payment-option ${selectedPaymentMethod === 'Online_Payment' ? 'selected' : ''}`}
                                            onClick={() => handleCheckboxChange('online', !isOnlineChecked)}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isOnlineChecked}
                                                onChange={(e) => handleCheckboxChange('online', e.target.checked)}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                            <span>💳 Online Payment</span>
                                        </div>
                                    </div>

                                    <button
                                        onClick={() => {
                                            if (selectedPaymentMethod === "Maamaas_Wallet") {
                                                handleWalletPayment();
                                            } else if (selectedPaymentMethod === "Online_Payment") {
                                                handleCheckoutClick();
                                            } else {
                                                toast.error("Select payment method");
                                            }
                                        }}
                                        disabled={
                                            orderPlacing ||
                                            !selectedPaymentMethod ||
                                            (selectedPaymentMethod === "Maamaas_Wallet" && !selectedWalletType)
                                        }
                                        className="cart-pay-btn"
                                    >
                                        {orderPlacing ? "Processing..." : "Pay Now"}
                                    </button>
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default CartSidebar;